/**
 * Async Helpers for Safe Promise Handling
 *
 * Provides utilities to prevent uncaught promise rejections in UI code,
 * with proper Sentry breadcrumb logging for debugging.
 */

import { addBreadcrumb } from '@sentry/react';
import { reduxLogger } from '@/core/utils/logger';

/**
 * Error context for async operations
 */
export interface AsyncErrorContext {
  /** Operation name for logging (e.g., 'toggleConversation', 'deleteContact') */
  operation: string;
  /** Additional data to include in breadcrumb */
  data?: Record<string, unknown>;
  /** Whether to rethrow the error after logging (default: false) */
  rethrow?: boolean;
}

/**
 * Determines if an error is a timeout error from RTK Query
 */
export function isTimeoutError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const err = error as { status?: string; error?: string | { message?: string } };

  // RTK Query timeout format
  if (err.status === 'TIMEOUT_ERROR') return true;

  // FETCH_ERROR with timeout message
  if (err.status === 'FETCH_ERROR') {
    const errorStr = typeof err.error === 'string'
      ? err.error
      : err.error?.message || '';
    return /timeout|timed out/i.test(errorStr);
  }

  return false;
}

/**
 * Safely unwrap an RTK Query mutation/query result with proper error handling.
 *
 * @param promise - The RTK Query mutation trigger result (before or after .unwrap())
 * @param context - Error context for logging
 * @returns The unwrapped result, or undefined if it failed
 *
 * @example
 * ```ts
 * const result = await safeUnwrap(
 *   updateContact(data).unwrap(),
 *   { operation: 'updateContact', data: { contactId: data.id } }
 * );
 * if (result) {
 *   // Success path
 * }
 * ```
 */
export async function safeUnwrap<T>(
  promise: Promise<T>,
  context: AsyncErrorContext
): Promise<T | undefined> {
  try {
    return await promise;
  } catch (error) {
    logAsyncError(error, context);
    if (context.rethrow) {
      throw error;
    }
    return undefined;
  }
}

/**
 * Wrap an async function to prevent uncaught rejections.
 * Useful for fire-and-forget operations that should still be logged.
 *
 * @param fn - Async function to wrap
 * @param context - Error context for logging
 * @returns A wrapped function that catches and logs errors
 *
 * @example
 * ```ts
 * const safeRefresh = handleAsync(
 *   () => dispatch(api.endpoints.getData.initiate()),
 *   { operation: 'refreshData' }
 * );
 * safeRefresh(); // Safe to call without await
 * ```
 */
export function handleAsync<T extends unknown[], R>(
  fn: (...args: T) => Promise<R>,
  context: AsyncErrorContext
): (...args: T) => Promise<R | undefined> {
  return async (...args: T): Promise<R | undefined> => {
    try {
      return await fn(...args);
    } catch (error) {
      logAsyncError(error, context);
      if (context.rethrow) {
        throw error;
      }
      return undefined;
    }
  };
}

/**
 * Suppress a promise's rejection with proper logging.
 * Use for fire-and-forget promises where you explicitly don't need the result.
 *
 * @param promise - Promise to suppress
 * @param context - Error context for logging
 *
 * @example
 * ```ts
 * // Fire-and-forget with proper error tracking
 * suppressAsync(
 *   pollAndInvalidateCache(campaignId),
 *   { operation: 'pollCampaign', data: { campaignId } }
 * );
 * ```
 */
export function suppressAsync(
  promise: Promise<unknown>,
  context: AsyncErrorContext
): void {
  void promise.catch((error) => {
    logAsyncError(error, context);
  });
}

/**
 * Safely execute multiple unwrap operations in parallel.
 * Unlike Promise.all with .unwrap() in a map, this doesn't create unhandled
 * rejections during the mapping phase.
 *
 * @param items - Array of items to process
 * @param operation - Async operation to perform on each item
 * @param context - Error context for logging
 * @returns Array of results with settled status
 *
 * @example
 * ```ts
 * const results = await safeParallelUnwrap(
 *   contactIds,
 *   (id) => deleteContact(id).unwrap(),
 *   { operation: 'bulkDelete' }
 * );
 * const succeeded = results.filter(r => r.status === 'fulfilled').length;
 * ```
 */
export async function safeParallelUnwrap<T, R>(
  items: T[],
  operation: (item: T) => Promise<R>,
  context: AsyncErrorContext
): Promise<PromiseSettledResult<R>[]> {
  const promises = items.map(async (item) => {
    try {
      return await operation(item);
    } catch (error) {
      logAsyncError(error, { ...context, data: { ...context.data, item } });
      throw error; // Re-throw so allSettled captures it as rejected
    }
  });

  return Promise.allSettled(promises);
}

/**
 * Log an async error with Sentry breadcrumb and console logging.
 * Used internally by the helpers, but exported for direct use when needed.
 */
export function logAsyncError(
  error: unknown,
  context: AsyncErrorContext
): void {
  const isTimeout = isTimeoutError(error);

  // Add Sentry breadcrumb for tracking
  addBreadcrumb({
    category: 'async.error',
    message: `${context.operation} failed${isTimeout ? ' (timeout)' : ''}`,
    level: isTimeout ? 'warning' : 'error',
    data: {
      operation: context.operation,
      isTimeout,
      errorType: (error as { status?: string })?.status || 'unknown',
      ...context.data,
    },
  });

  // Console logging for development
  if (isTimeout) {
    reduxLogger.warn(`[${context.operation}] Operation timed out`, {
      ...context.data,
      error,
    });
  } else {
    reduxLogger.error(`[${context.operation}] Operation failed`, {
      ...context.data,
      error,
    });
  }
}
